/**
 * @file validate.ts
 * @description Строгая проверка payload секции v2 перед сохранением
 * (phase19.16 Р2; SB64; db-design-r2 §1а — предохранитель 64 КБ). Сервер
 * отвечает 400 с `details: issues`, кабинет подсвечивает поле по `path`.
 *
 * Р2а (db-design-r2a, ответ 2б): проверка в контексте — на обычной странице
 * и в местах элементы данных товара запрещены; в шаблоне страницы товара
 * каждый из них не больше одного в блоке (по всему шаблону — `checkProductTemplate`).
 *
 * Лимит 30 секций на странице здесь не проверяется: правило захватывает
 * несколько строк, его держит сервер в транзакции (db-design-r2 §1а).
 */

import { SITE_LIMITS } from "../site-theme/limits.js";
import { isElementAllowedIn, PRODUCT_ELEMENT_INFO, type SectionContext } from "./contexts.js";
import { sectionErrorMap } from "./messages.js";
import { isProductElementKind, sectionPayloadSchema, type SectionPayload } from "./schema.js";

/** Замечание проверки: путь в payload (`columns.items.0.elements.2.label`) и текст словами владельца. */
export interface SectionIssue {
  path: string;
  message: string;
}

export type SectionValidation = { ok: true; payload: SectionPayload } | { ok: false; issues: SectionIssue[] };

export interface SectionValidateOptions {
  /** Где стоит секция; по умолчанию — обычная страница. */
  context?: SectionContext;
}

/** Размер JSON в байтах UTF-8 (без Buffer: пакет работает и в браузере). */
export function sectionPayloadBytes(input: unknown): number {
  const json = JSON.stringify(input) ?? "";
  return new TextEncoder().encode(json).length;
}

const issuePath = (path: readonly PropertyKey[]): string => path.map(String).join(".");

/** Название вида для сообщений: данные товара — по реестру, остальные — как есть. */
function kindLabel(kind: string): string {
  return isProductElementKind(kind) ? PRODUCT_ELEMENT_INFO[kind].label : kind;
}

/** Правила контекста: чужие виды и повтор элемента данных товара в блоке. */
export function sectionContextIssues(payload: SectionPayload, context: SectionContext): SectionIssue[] {
  const issues: SectionIssue[] = [];
  const seen = new Set<string>();
  payload.columns.items.forEach((column, col) => {
    column.elements.forEach((element, row) => {
      const path = `columns.items.${col}.elements.${row}`;
      if (!isElementAllowedIn(element.kind, context)) {
        issues.push({
          path: `${path}.kind`,
          message: `Элемент «${kindLabel(element.kind)}» можно поставить только в шаблон страницы товара`,
        });
        return;
      }
      if (!isProductElementKind(element.kind)) return;
      if (seen.has(element.kind)) {
        issues.push({ path: `${path}.kind`, message: `Элемент «${kindLabel(element.kind)}» уже стоит в этом блоке` });
      }
      seen.add(element.kind);
    });
  });
  return issues;
}

/** Проверка того, что прислал кабинет (создание, правка, вставка заготовки, импорт). */
export function validateSectionPayload(input: unknown, opts: SectionValidateOptions = {}): SectionValidation {
  const bytes = sectionPayloadBytes(input);
  if (bytes > SITE_LIMITS.sectionPayloadBytes) {
    return {
      ok: false,
      issues: [
        {
          path: "",
          message: `Блок слишком большой (${Math.ceil(bytes / 1024)} КБ, можно до ${SITE_LIMITS.sectionPayloadBytes / 1024} КБ) — разделите его на несколько`,
        },
      ],
    };
  }
  const parsed = sectionPayloadSchema.safeParse(input, { error: sectionErrorMap });
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({ path: issuePath(issue.path), message: issue.message })),
    };
  }
  const contextIssues = sectionContextIssues(parsed.data, opts.context ?? "page");
  if (contextIssues.length > 0) return { ok: false, issues: contextIssues };
  return { ok: true, payload: parsed.data };
}
